import { Box, Label, SelectMini, Tab, TabBar, Tabs, TabView } from '@salecycle/ui-component-library';
import * as RestApi from 'core/network/RestApi';
import { usePreferencesState } from 'core/preferences/usePreferencesContext';
import { ActivityState, Helpers } from 'entities/lifecycle';
import { AllActivityStates } from 'entities/lifecycle/activityState';
import * as React from 'react';
import { useState, useCallback } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useHistory, useParams, generatePath } from 'react-router-dom';
import { Site } from 'sections/client-management/entities';
import { IOffsiteCampaign } from 'sections/offsite-campaigns/entities/offsiteCampaign';
import styled from 'styled-components';
import { campaignStateToTranslation } from '../../utils/campaignStateToTranslation';
import { sortByAZ, sortByLast, sortByZA } from '../../utils/sortBy';
import { OffsiteCampaignEndpoints } from '../constants/api-endpoints';
import { OffsiteCampaignRoute } from '../routes/offsite-campaign-routes';
import OffsiteCampaignListItem from './components/OffsiteCampaignListItem';
import OffsiteCloneModal from './components/offsiteCloneModal';
import {
  useOffsiteCampaignsListDispatch,
  useOffsiteCampaignsListState,
} from './context/UseOffsiteCampaignsListContext';
import { StandardMainArea } from '@salecycle/experimental/StandardMainArea';
import { AgentChat } from '@salecycle/experimental/Agent/AgentChat';
import { useFeatureFlags } from 'core/app/AppContext';
import { FloatingButton } from '@salecycle/experimental/Agent/FloatingButton';
import { useOffsiteCampaignsCache } from '../hooks/useOffsiteCampaignsCache';
import { AgentFeature, AgentQueryResponse } from '@salecycle/experimental/Agent/types';
import { useGetOffsiteCampaignsQuery } from '../hooks/useGetOffsiteCampaignsQuery';
import humanizeDuration from 'humanize-duration';

type ICampaignTabId = ActivityState | 'All';

const StyledCampaignsList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin-bottom: 10rem;
`;

const sortByOptions = (t: TFunction<'campaigns'>) => [
  {
    value: 'LAST_MODIFIED',
    label: t('table.filters.modified'),
  },
  {
    value: 'NAME_AZ',
    label: t('table.filters.name'),
  },
  {
    value: 'NAME_ZA',
    label: t('table.filters.nameReverse'),
  },
];

const sortCampaigns = (a: IOffsiteCampaign, b: IOffsiteCampaign, sortBy) => {
  switch (sortBy.value) {
    case 'LAST_MODIFIED':
      return sortByLast(a.updatedAt, b.updatedAt);
    case 'NAME_AZ':
      return sortByAZ(a.name, b.name);
    case 'NAME_ZA':
      return sortByZA(a.name, b.name);
  }
};

const tabIdToTranslation = (key: ICampaignTabId, t: TFunction<'campaigns'>) => {
  switch (key) {
    case 'All':
      return t('allTab');
    default:
      return campaignStateToTranslation(key as ActivityState, t);
  }
};

const filterCampaignsByTabId = (campaigns: IOffsiteCampaign[], tabId: ICampaignTabId) => {
  if (tabId === 'All') {
    return campaigns;
  }
  return campaigns.filter(campaign => Helpers.getActivityState(campaign) === tabId);
};

export const OffsiteCampaignsList: React.FC = () => {
  const dispatch = useOffsiteCampaignsListDispatch();
  const { siteId } = useParams<{ siteId: string }>();
  const { campaigns, originalCampaigns, showCloneModal, site } = useOffsiteCampaignsListState();
  const campaignsQuery = useGetOffsiteCampaignsQuery(siteId);

  const siteQuery = useQuery(['site', siteId], async () => {
    const result = await RestApi.get<Site>(generatePath(OffsiteCampaignEndpoints.GET_SITE, { siteId }));
    return result.data;
  });

  // Initial site load
  React.useEffect(() => {
    if (!siteQuery.isLoading) {
      const loadedSite: Site = siteQuery?.data ?? null;
      dispatch({
        type: 'SET_SITE_INITIAL',
        payload: { site: loadedSite },
      });
    }
  }, [siteQuery.isLoading, dispatch, siteQuery.data]);

  // Initial campaigns list
  React.useEffect(() => {
    if (!campaignsQuery.isLoading && !campaignsQuery.isError) {
      const loadedCampaigns: IOffsiteCampaign[] = campaignsQuery?.data ?? null;
      dispatch({
        type: 'SET_CAMPAIGNS_INITIAL',
        payload: { campaigns: loadedCampaigns },
      });
    }
  }, [campaignsQuery.isLoading, campaignsQuery.isError, dispatch, campaignsQuery.data]);

  // Update displayed list whenever a campaign changes
  React.useEffect(() => {
    if (JSON.stringify(campaigns) !== JSON.stringify(originalCampaigns)) {
      const unsavedCampaigns = JSON.parse(JSON.stringify(campaigns)) as IOffsiteCampaign[];
      dispatch({
        type: 'UNSAVED_CAMPAIGNS_CHANGES',
        payload: { campaigns: unsavedCampaigns },
      });
    }
  }, [campaigns, originalCampaigns, dispatch]);

  const { flagEnabled } = useFeatureFlags();
  const { t } = useTranslation('campaigns');
  const { push } = useHistory();
  const { timezone } = usePreferencesState();
  const tabIds = ['All'].concat(AllActivityStates);
  const [activeTabId, setActiveTabId] = useState<ICampaignTabId>('All');
  const [sortBy, setSortBy] = useState(sortByOptions(t)[0]);
  const [isAgentOpen, setIsAgentOpen] = useState(false);
  const { addCampaignToCache } = useOffsiteCampaignsCache(siteId);

  const toggleAgent = useCallback(() => setIsAgentOpen(prev => !prev), []);
  const updateCampaignList = useCallback(
    (data: AgentQueryResponse['data']) => data?.campaign?.id && addCampaignToCache(data.campaign),
    [addCampaignToCache],
  );
  const newCampaign = () =>
    push(generatePath(OffsiteCampaignRoute.CREATE_OFFSITE_WIZARD_PAGE, { siteId, campaignWizardPage: 'feature' }));

  return (
    <>
      <StandardMainArea
        isLoading={campaignsQuery.isLoading}
        isError={campaignsQuery.isError}
        errorHeading={t('offsite.errorLoadingCampaigns')}
        heading={t('offsite.heading')}
        headingButtonText={t('newCampaign')}
        headingButtonDisabled={!site?.unsubscribeSettings}
        onHeadingButtonClicked={newCampaign}
        isAgentOpen={isAgentOpen}
        agentComponent={
          flagEnabled('ld-campy-assistant') && (
            <AgentChat
              siteId={siteId}
              feature={AgentFeature.Offsite}
              onClose={toggleAgent}
              onResponse={updateCampaignList}
              filters={{
                session_timeout: site?.sessionTime ? humanizeDuration(site.sessionTime) : undefined,
              }}
            />
          )
        }
      >
        {!campaignsQuery.isLoading && !campaignsQuery.isError && campaigns.length > 0 ? (
          <Tabs fillHeight={true}>
            <TabBar>
              {tabIds.map((id: ICampaignTabId) => (
                <Tab
                  tabId={id}
                  key={id}
                  badgeLabel={String(filterCampaignsByTabId(campaigns, id).length)}
                  onClick={() => {
                    setActiveTabId(id);
                  }}
                >
                  {tabIdToTranslation(id, t)}
                </Tab>
              ))}
              <Box margin="0 2rem 0 0">
                <Label name={`${t('sortBy')}:`} marginRight={true} />
                <SelectMini
                  ariaLabel={t('sortBy')}
                  value={sortBy}
                  options={sortByOptions(t)}
                  onChange={selectedOption => setSortBy(selectedOption)}
                  menuPlacement="right"
                />
              </Box>
            </TabBar>

            <TabView tabId={activeTabId} padding={0}>
              <StyledCampaignsList>
                {filterCampaignsByTabId(
                  [...campaigns].sort((a, b) => sortCampaigns(a, b, sortBy)),
                  activeTabId,
                ).map((campaign: IOffsiteCampaign) => (
                  <OffsiteCampaignListItem campaign={campaign} key={campaign.id} timezone={timezone} />
                ))}
              </StyledCampaignsList>
            </TabView>
          </Tabs>
        ) : (
          <Box stackItems={true} horizontalPositioning="center" padding="40px 0">
            <h3>{t('noCampaigns')}</h3>
          </Box>
        )}
      </StandardMainArea>
      {flagEnabled('ld-campy-assistant') && !isAgentOpen && (
        <FloatingButton onClick={toggleAgent} data-testid="floating-button">
          <img src="/images/campy_small.png" alt={t('campaignAssistant.agentName')} width={60} height={60} />
        </FloatingButton>
      )}
      {showCloneModal && <OffsiteCloneModal />}
    </>
  );
};

export default OffsiteCampaignsList;
